import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { getPort } from "../utils/getPort";
// -----------------------------------------------------------
// Floating Panel
// -----------------------------------------------------------
const Panel = styled.div`
  position: fixed;
  top: ${(props) => props.y}px;
  left: ${(props) => props.x}px;
  z-index: 2147483647;
  background: white;
  border: 1px solid #ddd;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  min-width: 600px;
`;

const TitleBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background: #f5f5f5;
  border-bottom: 1px solid #ddd;
  border-radius: 8px 8px 0 0;
  cursor: move;
  user-select: none;
  font-weight: bold;
  font-size: 14px;
`;

const TitleText = styled.span`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-right: 8px;
`;

const IconButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 2px 8px;
  font-size: 16px;
  line-height: 1;
  border-radius: 4px;

  &:hover {
    background: #e0e0e0;
  }
`;

const Body = styled.div`
  overflow: auto;
  flex: 1;
  min-height: 0;
`;

// -----------------------------------------------------------
// Grid
// -----------------------------------------------------------
const GridContainer = styled.div`
  display: grid;
  grid-template-columns: 120px repeat(8, 1fr);
  gap: 4px;
  padding: 10px;
`;

const HeaderCell = styled.div`
  font-weight: bold;
  text-align: center;
  padding: 6px;
  background: #f0f0f0;
  border-radius: 6px;
`;

const RowLabel = styled.div`
  font-weight: bold;
  text-align: right;
  padding: 6px;
  background: #fafafa;
  border-radius: 6px;
`;

const Cell = styled.div`
  padding: 6px;
  border-radius: 6px;
  text-align: center;
  font-weight: bold;
  color: ${(props) => props.textColor};
  background: ${(props) => props.bg};
  user-select: none;
`;

// -----------------------------------------------------------
// Color Gradient
// -----------------------------------------------------------
function colorForPct(pct) {
  if (pct === null || pct === "N/A") return "#ccc";
  if (pct >= 10) return "#008000";
  if (pct >= 5) return "#33a533";
  if (pct >= 1) return "#66c266";
  if (pct >= -3) return "#4d79ff";
  if (pct >= -7) return "#ff9999";
  if (pct >= -12) return "#ff4d4d";
  return "#cc0000";
}

function textColor(bg) {
  const c = bg.replace("#", "");
  const r = parseInt(c.substr(0, 2), 16);
  const g = parseInt(c.substr(2, 2), 16);
  const b = parseInt(c.substr(4, 2), 16);
  const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
  return luminance > 140 ? "black" : "white";
}

// -----------------------------------------------------------
// Main Component
// -----------------------------------------------------------
export default function Heatmap({ symbol, market, onClose }) {
  const [data, setData] = useState(null);
  const [minimized, setMinimized] = useState(false);

  // Position (top of screen, roughly centered)
  const [pos, setPos] = useState({ x: 20, y: 10 });
  const dragState = useRef(null);

  useEffect(() => {
    if (!symbol || !market) return;

    async function load() {
      try {
        const port = getPort(market);
        const res = await fetch(
          `${port}/api/heatmap/${market}/${symbol}`
        );
        const json = await res.json();
        setData(json);
      } catch (err) {
        console.error("Heatmap load error:", err);
      }
    }

    load();
  }, [symbol, market]);

  // ---- Drag handlers ----
  function onMouseDown(e) {
    // Ignore drags started on the buttons
    if (e.target.closest("button")) return;

    dragState.current = {
      startX: e.clientX,
      startY: e.clientY,
      origX: pos.x,
      origY: pos.y,
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  }

  function onMouseMove(e) {
    const s = dragState.current;
    if (!s) return;

    const dx = e.clientX - s.startX;
    const dy = e.clientY - s.startY;

    // Keep it on-screen
    const newX = Math.max(0, Math.min(window.innerWidth - 100, s.origX + dx));
    const newY = Math.max(0, Math.min(window.innerHeight - 40, s.origY + dy));

    setPos({ x: newX, y: newY });
  }

  function onMouseUp() {
    dragState.current = null;
    window.removeEventListener("mousemove", onMouseMove);
    window.removeEventListener("mouseup", onMouseUp);
  }

  const offsets = [20, 40, 60, 80, 100, 120, 140, 160];

  const title = data
    ? `Heat Map — ${data.symbol} — ${data.name}`
    : "Heat Map — loading…";

  return (
    <Panel x={pos.x} y={pos.y}>
      <TitleBar onMouseDown={onMouseDown}>
        <TitleText>{title}</TitleText>
        <IconButton
          onClick={() => setMinimized((m) => !m)}
          title={minimized ? "Restore" : "Minimize"}
        >
          {minimized ? "▢" : "—"}
        </IconButton>
        {onClose && (
          <IconButton onClick={onClose} title="Close">
            ×
          </IconButton>
        )}
      </TitleBar>

      {!minimized && (
        <Body>
          {!data ? (
            <div style={{ padding: 20 }}>Loading heatmap...</div>
          ) : (
            <GridContainer>
              <HeaderCell></HeaderCell>
              {offsets.map((off) => (
                <HeaderCell key={off}>{off}</HeaderCell>
              ))}

              {data.timeframes.map((tf) => (
                <React.Fragment key={tf.label}>
                  <RowLabel>{tf.label}</RowLabel>

                  {tf.values.map((cell, idx) => {
                    if (cell === "N/A") {
                      return (
                        <Cell key={idx} bg="#ccc" textColor="black">
                          N/A
                        </Cell>
                      );
                    }

                    const pct = cell.pct;
                    const rank = cell.rank;
                    const val = cell.val;

                    const bg = colorForPct(pct);
                    const tc = textColor(bg);

                    return (
                      <Cell key={idx} bg={bg} textColor={tc}>
                        {pct.toFixed(2)}%
                        <div style={{ fontSize: "11px", opacity: 0.85 }}>
                          {rank}
                        </div>
                        <div style={{ fontSize: "11px", opacity: 0.75 }}>
                          {val !== null ? val.toFixed(3) : ""}
                        </div>
                      </Cell>
                    );
                  })}
                </React.Fragment>
              ))}
            </GridContainer>
          )}
        </Body>
      )}
    </Panel>
  );
}
